import { useEffect, useState } from 'react';
import type { ClientChangeRequestListDto, ClientWorkOrderListDto } from '@xcentral/api-client';
import { useApi } from '../hooks/useApi';

/**
 * #20979 (BR-T-F33-001) — the counts shown as badges on the client nav.
 *
 * Each source names the nav item it badges (by path), the list endpoint it reads, and how a count is taken from
 * that list's response. #20977 (Change approvals) adds its own source to {@link NAV_BADGE_SOURCES}; nothing else in
 * the shell changes.
 *
 * Rules:
 * - every source is fetched ONCE per layout mount — not per navigation, not polled;
 * - a failed fetch renders NO badge and raises no error: the nav item still shows, only without a count;
 * - a count of zero renders no badge (the value is omitted, not 0).
 */
export interface NavBadgeSource {
  /** The nav item's `path` this count badges. */
  path: string;
  /** The list endpoint read for the count. */
  url: string;
  /** The count, from that endpoint's response. */
  count: (body: unknown) => number;
}

export const NAV_BADGE_SOURCES: readonly NavBadgeSource[] = [
  {
    path: '/work-orders',
    url: '/api/v1/client-it/work-orders',
    count: (body) => (body as ClientWorkOrderListDto | null)?.awaitingCount ?? 0,
  },
  {
    // #20977 (BR-A-F32-001) — awaitingCount ignores the list's filter and paging, so one row is enough.
    path: '/change-approvals',
    url: '/api/v1/client-it/change-requests?pageSize=1',
    count: (body) => (body as ClientChangeRequestListDto | null)?.awaitingCount ?? 0,
  },
];

/** Badge counts keyed by nav path; a path is absent when its count is zero or its fetch failed. */
export type NavBadges = Readonly<Record<string, number>>;

export function useNavBadges(sources: readonly NavBadgeSource[] = NAV_BADGE_SOURCES): NavBadges {
  const api = useApi();
  const [badges, setBadges] = useState<NavBadges>({});

  useEffect(() => {
    const ctrl = new AbortController();
    for (const source of sources) {
      void (async () => {
        try {
          const body = await api.get<unknown>(source.url, ctrl.signal);
          if (ctrl.signal.aborted) return;
          const n = source.count(body);
          if (Number.isFinite(n) && n > 0) {
            setBadges((prev) => ({ ...prev, [source.path]: n }));
          }
        } catch {
          // No badge on a failed fetch, and nothing surfaced: the screen itself owns the error state.
        }
      })();
    }
    return () => ctrl.abort();
    // `api` is a stable memoised client and `sources` a module constant, so this runs once per mount.
  }, [api, sources]);

  return badges;
}
